import React, { HTMLAttributeAnchorTarget } from 'react';
import { Definition, UnknownSpec } from './definitions';
import { Display } from './display';
import { Labels } from './labels';
import { TimeSeriesValueTuple } from './time-series-queries';

type EventShape = 'circle' | 'rect' | 'roundRect' | 'triangle' | 'diamond' | 'pin' | 'arrow' | 'none';
type ImageURL = string;
export type EventIcon = EventShape | ImageURL;

export interface EventCategory {
  name: string;
  icon?: React.ReactElement;
}

export interface EventLink {
  label: string;
  queryParams: string;
  target?: HTMLAttributeAnchorTarget;
}

interface BaseEvent {
  /**
   * When the event was created.
   */
  timestampMs: number;

  /**
   * Optional end timestamp for the event.
   */
  endTimestampMs?: number;

  /**
   * Descriptive title of the event.
   */
  title: string;

  /**
   * Color of the event.
   */
  color?: string;

  /**
   * Icon of the event.
   * Supported icons: 'circle', 'rect', 'roundRect', 'triangle', 'diamond', 'pin', 'arrow', 'none'
   * It can be set to an image with 'image://url' , in which URL is the link to an image, or dataURI of an image.
   * It can also be set to arbitrary vector path via 'path://' in ECharts.
   * https://echarts.apache.org/en/option.html#series-scatter.symbol
   */
  icon?: EventIcon;

  /**
   * Optional labels for the event.
   */
  labels?: Labels;

  /**
   * Link.
   */
  link?: EventLink;
}

export interface EventPayload extends BaseEvent {
  /**
   * Event category
   */
  category?: EventCategory;
}

export interface EventGroupPayload extends BaseEvent {
  /**
   * Total number of events
   */
  totalCount: number;
  /**
   * List of event categories
   */
  eventCategories: Array<EventCategory & { count: number }>;
}

export type EventItem = EventPayload | EventGroupPayload;

export interface EventSpec {
  name: string;
  display?: Display & {
    hidden?: boolean;
  };
  /**
   * Whether the event is enabled.
   * @default true
   */
  enabled?: boolean;
}

export interface QueryEventDefinition<PluginSpec = UnknownSpec> extends Definition<QueryEventSpec<PluginSpec>> {
  kind: 'QueryEvent';
}

export interface QueryEventSpec<PluginSpec = UnknownSpec> extends EventSpec {
  plugin: Definition<PluginSpec>;
}

export type EventDefinition = QueryEventDefinition;

export type EventSeriesOption = {
  value: TimeSeriesValueTuple;
  event?: EventPayload;
  symbol?: string;
  symbolOffset?: number[];
  itemStyle?: {
    color?: string;
    opacity?: number;
  };
};

export const EVENT_SERIES_NAME = 'Event Series';

export type EventWithEndTimestamp = Omit<EventItem, 'endTimestampMs'> & { endTimestampMs: number };

/**
 * Helper function to check if an event has an end timestamp
 */
export function isEventWithEndTimestamp(event: EventItem): event is EventWithEndTimestamp {
  return 'endTimestampMs' in event && event.endTimestampMs !== undefined && event.endTimestampMs !== event.timestampMs;
}
